import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const IssueList = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({});
  const [users, setUsers] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter states
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    priority: searchParams.get('priority') || '',
    assigned_to: searchParams.get('assigned_to') || '',
    page: parseInt(searchParams.get('page')) || 1
  });

  useEffect(() => {
    fetchIssues();
    fetchUsers();
  }, [filters]);

  useEffect(() => {
    // Update URL when filters change
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.set(key, filters[key]);
      }
    });
    setSearchParams(params);
  }, [filters, setSearchParams]);

  const fetchIssues = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.set(key, filters[key]);
        }
      });

      const response = await axios.get(`/issues?${params.toString()}`);
      setIssues(response.data.issues);
      setPagination({
        total: response.data.total,
        pages: response.data.pages,
        current_page: response.data.current_page
      });
    } catch (error) {
      setError('Failed to load issues');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users');
      setUsers(response.data);
    } catch (error) {
      // Users endpoint might be admin-only, ignore error
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({
      ...prev,
      page
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      priority: '',
      assigned_to: '',
      page: 1
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      'Open': 'badge-open',
      'In Progress': 'badge-progress',
      'Closed': 'badge-closed',
      'Resolved': 'badge-closed'
    };
    return statusClasses[status] || 'badge-open';
  };

  const getPriorityBadgeClass = (priority) => {
    const priorityClasses = {
      'Low': 'badge-low',
      'Medium': 'badge-medium',
      'High': 'badge-high',
      'Critical': 'badge-critical'
    };
    return priorityClasses[priority] || 'badge-medium';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="main-content">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Issues</h1>
          <p className="text-gray-600">Manage and track all issues</p>
        </div>
        <Link to="/create-issue" className="btn btn-primary">
          Create New Issue
        </Link>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="card-body">
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div>
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search issues..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>

            <div>
              <label className="form-label">Status</label>
              <select
                className="form-control form-select"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Closed">Closed</option>
                <option value="Resolved">Resolved</option>
              </select>
            </div>

            <div>
              <label className="form-label">Priority</label>
              <select
                className="form-control form-select"
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
              >
                <option value="">All Priorities</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>

            <div>
              <label className="form-label">Assigned To</label>
              <select
                className="form-control form-select"
                value={filters.assigned_to}
                onChange={(e) => handleFilterChange('assigned_to', e.target.value)}
              >
                <option value="">All Users</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <button
              onClick={clearFilters}
              className="btn btn-outline btn-sm"
            >
              Clear Filters
            </button>
            <div className="text-sm text-gray-600">
              {pagination.total} total issues
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="spinner"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {/* Issues List */}
      {!loading && !error && (
        <>
          {issues.length > 0 ? (
            <div className="card">
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Title</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Assigned To</th>
                      <th>Creator</th>
                      <th>Created</th>
                      <th>Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issues.map(issue => (
                      <tr key={issue.id}>
                        <td>#{issue.id}</td>
                        <td>
                          <Link 
                            to={`/issues/${issue.id}`}
                            className="font-medium text-primary-color hover:underline"
                          >
                            {issue.title}
                          </Link>
                          <div className="text-sm text-gray-600">
                            {issue.comments_count} comments, {issue.attachments_count} attachments
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${getPriorityBadgeClass(issue.priority)}`}>
                            {issue.priority}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${getStatusBadgeClass(issue.status)}`}>
                            {issue.status}
                          </span>
                        </td>
                        <td>
                          {issue.assignee_name || (
                            <span className="text-gray-400">Unassigned</span>
                          )}
                        </td>
                        <td>{issue.creator_name}</td>
                        <td>{formatDate(issue.created_at)}</td>
                        <td>{formatDate(issue.updated_at)}</td>
                        <td>
                          <div className="flex gap-2">
                            <Link 
                              to={`/issues/${issue.id}`}
                              className="btn btn-outline btn-sm"
                            >
                              View
                            </Link>
                            <Link 
                              to={`/issues/${issue.id}/edit`}
                              className="btn btn-secondary btn-sm"
                            >
                              Edit
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body text-center py-12">
                <h3 className="text-lg font-semibold mb-2">No issues found</h3>
                <p className="text-gray-600 mb-4">
                  {Object.values(filters).some(f => f) 
                    ? 'Try adjusting your filters to see more results.' 
                    : 'Get started by creating your first issue.'
                  }
                </p>
                <Link to="/create-issue" className="btn btn-primary">
                  Create New Issue
                </Link>
              </div>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className="btn btn-outline btn-sm"
              >
                Previous
              </button>
              
              <span className="text-sm text-gray-600">
                Page {pagination.current_page} of {pagination.pages}
              </span>
              
              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.pages}
                className="btn btn-outline btn-sm"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default IssueList; 